/* eslint-disable react/prop-types */

import React from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Typography,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import FileListItem from './FileListItem';

const FileUpload = ({
  value,
  onChange,
  sx,
  title,
  buttonText,
  typographyProps,
  buttonProps,
  disabled,
  maxSize,
  dense,
  highlightOnDrag,
  dragActiveSx,
  activeTitle,
  minHeight,
  showFileList,
  condensed,
  ...options
}) => {
  const {
    fileRejections,
    getRootProps,
    getInputProps,
    open,
    isDragActive,
  } = useDropzone({
    ...options,
    disabled,
    maxSize,
    onDropAccepted: (newFiles) => onChange([...value, ...newFiles]),
    noClick: true,
    noKeyboard: true,
  });

  const isFileTooLarge = maxSize !== undefined && fileRejections.length > 0 && fileRejections[0].file.size > maxSize;

  const remove = (index) => {
    const files = [...value];
    files.splice(index, 1);
    onChange(files);
  };

  const files = value?.map((file, i) => <FileListItem key={file.name} name={file.name} onDelete={() => remove(i)} />);

  let resolvedMinHeight = minHeight;
  if (!resolvedMinHeight) {
    resolvedMinHeight = 180;
    if (dense) {
      resolvedMinHeight = 120;
    }
  }

  let paddingYValue = 3;
  if (dense) {
    paddingYValue = 1;
  }

  let hoverBorderColor;
  if (!disabled) {
    hoverBorderColor = 'text.primary';
  }

  let activeHighlight = {};
  if (highlightOnDrag && isDragActive) {
    activeHighlight = {
      borderColor: 'primary.main',
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
      boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.15)',
      ...dragActiveSx,
    };
  }

  const rootStyles = {
    border: 2,
    backgroundColor: 'rgba(47,72,88,0.08)',
    borderRadius: 1,
    borderColor: 'rgba(0, 0, 0, 0.2)',
    minHeight: resolvedMinHeight,
    paddingY: paddingYValue,
    paddingX: 1,
    '&:hover': { borderColor: hoverBorderColor },
    '&:focus-within': {
      borderColor: 'primary.main',
      borderWidth: 2,
    },
    ...activeHighlight,
    ...sx,
  };

  let uploadIconColor = 'primary';
  if (disabled) {
    uploadIconColor = 'disabled';
  }
  let dropPrompt = title;
  if (isDragActive && activeTitle) {
    dropPrompt = activeTitle;
  }

  return (
    <Box
      {...getRootProps()}
      sx={rootStyles}
      data-drag-active={isDragActive}
    >
      <FormControl
        error={isFileTooLarge}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon
          sx={{ fontSize: 40 }}
          color={uploadIconColor}
        />
        <Typography variant="caption" textAlign="center" sx={{ paddingBottom: 1 }} {...typographyProps}>
          {dropPrompt}
        </Typography>
        <Button variant="contained" onClick={open} disabled={disabled} sx={{ marginBottom: 1 }} {...buttonProps}>
          {buttonText}
        </Button>
        <FormHelperText> {fileRejections[0]?.errors[0]?.message} </FormHelperText>
      </FormControl>
      {
        showFileList && value && value.length > 0 && (
          <Box
            component="ul"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              listStyle: 'none',
              p: 0.5,
              m: 0,
            }}
          >
            {files}
          </Box>
        )
      }
    </Box>
  );
};

FileUpload.defaultProps = {
  title: "Drag 'n' drop some files here, or click to select files",
  buttonText: 'Upload',
  dense: false,
  highlightOnDrag: false,
  dragActiveSx: {},
  minHeight: null,
  showFileList: true,
};

export default FileUpload;
